import React from 'react';
import { cn } from '@/utils/utils';
import { SEGMENTED, SEGMENTED_BAR, SEGMENTED_PILLS, SEGMENTED_SIDE, SEGMENTED_TABS, segmentedBar, segmentedItem, segmentedPill, segmentedSide, segmentedTab } from '@/components/ui/recipes';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

export interface SegmentedOption<T extends string> {
  id: T;
  label: React.ReactNode;
  /** An option that can't be chosen here. */
  disabled?: boolean;
  /** What it is, or why it can't be chosen, shown on hover and focus. */
  tooltip?: string;
}

interface SegmentedProps<T extends string> {
  options: ReadonlyArray<SegmentedOption<T>>;
  value: T;
  // NoInfer: the options name the choices, so a setState passed here doesn't widen them to string
  onChange: (id: NoInfer<T>) => void;
  /** What the group chooses, for screen readers. */
  label?: string;
  /** The whole control can't be changed right now. */
  disabled?: boolean;
  /** Fills its row, the options sharing the width. */
  stretch?: boolean;
  /** tray: small options in a tinted tray (a panel's setting); pills: round buttons in a bordered tray (a page's view switch); tabs: an editor panel's tabs, no tray, the open one filled in the brand ink; side: a list's sources standing upright at its right, the open one framed (the animation list's Imported and Stock); bar: a pill in the line split by hairlines, the chosen one tinted in the brand ink (the shape editor's FFD mode); bar-solid: the same, the chosen one filled (a trace's image plane). */
  variant?: 'tray' | 'pills' | 'tabs' | 'side' | 'bar' | 'bar-solid';
  className?: string;
}

/** Lureo's segmented control: one choice from a few, side by side. */
export function Segmented<T extends string>({ options, value, onChange, label, disabled, stretch, variant = 'tray', className }: SegmentedProps<T>) {
  const bar = variant === 'bar' || variant === 'bar-solid';
  return (
    <div role="group" aria-label={label} className={cn(variant === 'pills' ? SEGMENTED_PILLS : variant === 'tabs' ? SEGMENTED_TABS : variant === 'side' ? SEGMENTED_SIDE : bar ? SEGMENTED_BAR : SEGMENTED, stretch && 'flex w-full', className)}>
      {options.map((o, i) => {
        const button = (
          <button
            key={o.id}
            type="button"
            aria-pressed={value === o.id}
            disabled={disabled || o.disabled}
            onClick={() => onChange(o.id)}
            className={cn(variant === 'pills' ? segmentedPill(value === o.id) : variant === 'tabs' ? segmentedTab(value === o.id) : variant === 'side' ? segmentedSide(value === o.id) : bar ? segmentedBar(value === o.id, variant === 'bar-solid') : segmentedItem(value === o.id), stretch && 'flex-1 justify-center')}
          >
            {o.label}
          </button>
        );
        const option = !o.tooltip ? button : (
          <Tooltip key={o.id}>
            <TooltipTrigger asChild>
              {/* A disabled button gets no pointer events, so the span carries the tooltip */}
              <span className={cn('inline-flex h-full', stretch && 'flex-1 [&>button]:w-full')} tabIndex={o.disabled ? 0 : undefined}>
                {button}
              </span>
            </TooltipTrigger>
            <TooltipContent side="bottom">{o.tooltip}</TooltipContent>
          </Tooltip>
        );
        // A bar's options are split by a short hairline
        if (!bar || i === 0) return option;
        return (
          <React.Fragment key={o.id}>
            <span aria-hidden className="h-3.5 w-px shrink-0 bg-lureo-line" />
            {option}
          </React.Fragment>
        );
      })}
    </div>
  );
}
